import './update-coupon-dates.scss'

import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { DatePicker, Button, Form, FormInstance, notification, Popconfirm } from 'antd'
import moment, { Moment } from 'moment'
import React, { useEffect } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../api'
import { config } from '../../../config'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { getOrdinalNumber } from '../../../formatters'
import { httpClient } from '../../../util/http-client'
import { messages } from '../messages'
import { convertCouponDatesFormValuesToCouponDatesData, UpdateCouponDatesFormValues } from '../models'

const { isAfter, newDate } = momentDateHandler

interface Props {
  tradeId: number
  domain: string
  onSuccess?: (tradeId: number) => void
}

export const UpdateCouponDates = ({ tradeId, domain, onSuccess }: Props) => {
  const formReference = React.useRef<FormInstance>()

  const { data: trade } = useSWR(tradeId ? [`/trades/${tradeId}`, domain] : null, fetchDetailByDomain, {
    suspense: true,
  })
  const { data: currentCouponDates } = useSWR(
    () => (trade?.id ? [`/trades/${trade.id}/coupon-dates`, domain] : null),
    fetchDetailByDomain,
    { suspense: true },
  )

  useEffect(() => {
    if (!currentCouponDates) return

    const couponDates = currentCouponDates.list.map((couponDate: Date) => ({ couponDate: moment(couponDate) }))
    formReference.current?.setFieldsValue({ couponDates })
  }, [currentCouponDates])

  const onSubmit = async ({ couponDates }: UpdateCouponDatesFormValues) => {
    try {
      await httpClient(`/trades/${tradeId}/coupon-dates`, {
        method: 'PUT',
        domain,
        data: { dates: convertCouponDatesFormValuesToCouponDatesData({ couponDates }) },
      })

      notification.success({
        message: messages.common.success,
        description: messages.couponDates.update.success,
      })

      onSuccess(tradeId)
    } catch (error) {
      notification.error({ message: messages.common.error, description: messages.couponDates.update.error })
    }
  }

  return (
    <Form ref={formReference} name="update-coupon-dates-form" className="update-coupon-dates" onFinish={onSubmit}>
      <Form.List name="couponDates">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, fieldKey, ...field }) => (
              <Row key={key}>
                <Form.Item
                  {...field}
                  label={`${getOrdinalNumber(name + 1)} Coupon Date`}
                  name={[name, 'couponDate']}
                  fieldKey={[fieldKey, 'couponDate']}
                  className="coupon-date"
                >
                  <DatePicker
                    name="couponDate"
                    format={config.defaultDateFormat}
                    defaultValue={newDate(trade?.maturityDate)}
                    disabledDate={(date: Moment) =>
                      trade && isAfter({ firstDate: date, secondDate: newDate(trade?.maturityDate) })
                    }
                  />
                </Form.Item>
                <Button type="link" onClick={() => remove(name)} icon={<CloseCircleOutlined />} />
              </Row>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                Add field
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item name="actions">
        <Column>
          <Popconfirm
            title="Are you sure you want to update the coupon dates?"
            onConfirm={() => formReference.current?.submit()}
          >
            <Button htmlType="submit" type="primary">
              Update Coupon Dates
            </Button>
          </Popconfirm>
        </Column>
      </Form.Item>
    </Form>
  )
}
